import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { ErreurApi } from '../api/client';
import { chezHiolle, estFactureSellsy, type EtapeCochable } from '@mosaik/core';
import { useAttendreReglement, useCocherEtape, useReleverPaiement } from '../api/hooks';
import type { CommandeDetaillee, EtatEnvoiCommande } from '../api/types';
import { dateHeure, formatCourt } from '../format';
import { avisEchu, CaseAvis } from './CaseAvis';
import { RelancesReglementDeposees } from './MailDepose';
import { RelanceReglement } from './RelanceReglement';
import { Repousser } from './Repousser';
import { useToasts } from './Toasts';

/**
 * Où en est la commande, en six cases — sept chez HIOLLE, où la demande
 * d'enlèvement DHL est à nous.
 *
 * Remplace la barre de progression de la fiche : une barre dit « à peu près
 * aux deux tiers », une liste dit CE QUI est fait et ce qui ne l'est pas. Les
 * cases se cochent d'elles-mêmes quand l'étape avance, en lisant l'état de la
 * commande.
 *
 * Elles se cochent AUSSI à la main — un règlement reçu par chèque, une commande
 * passée au téléphone. La coche manuelle prime alors sur les données dans le
 * calcul (« À faire », actions dues), sans réécrire aucun champ, et elle se
 * signale par sa couleur — qui l'a posée et quand se lit au survol. Recliquer
 * pour revenir à ce que disent les données efface la coche.
 *
 * Deux cibles par ligne, et c'est voulu : la CASE coche, le LIBELLÉ ouvre le
 * geste de l'étape — envoyer le mail s'il reste à faire, relire celui qui est
 * parti sinon.
 */
export interface GestesEtapes {
  onReglement: () => void;
  onFournisseur: () => void;
  /** Ouvre la demande d'enlèvement DHL. La case n'existe que chez HIOLLE. */
  onEnlevement: () => void;
  onExpedition: () => void;
  onTracking: () => void;
  /** Absent : l'étape n'a pas de geste (commande déjà livrée). */
  onLivre?: () => void;
  onAvis: () => void;
  /**
   * « Livré » ne se force pas comme les autres : cocher clôture la commande
   * (avec sa confirmation), décocher la remet en cours.
   */
  onBasculerLivre: () => void;
}

export function EtapesCommande({
  commande, gestes,
}: {
  commande: CommandeDetaillee;
  gestes: GestesEtapes;
}) {
  const { paiement, envois, avis, relanceReglement, etapes: cases } = commande;
  const cocher = useCocherEtape();
  const attente = useAttendreReglement();
  const { ajouter } = useToasts();

  /*
   * Attendre le règlement : proposé tant que la facture n'est pas tenue pour
   * réglée et que la commande n'est pas partie chez le fournisseur — après,
   * attendre ne retiendrait plus rien.
   */
  const enAttenteReglement = Boolean(commande.attenteReglement)
    && !cases.reglement.fait && !cases.fournisseur.fait;
  // Le relevé ne cherche que les factures Sellsy : attendre le règlement d'une
  // commande Shopify ne se lèverait jamais de soi-même.
  const sellsy = estFactureSellsy(commande.ref);
  const attenteProposable = sellsy && !cases.reglement.fait && !cases.fournisseur.fait;
  const basculerAttente = async () => {
    try {
      await attente.mutateAsync({ id: commande.id, attendre: !commande.attenteReglement });
      ajouter(commande.attenteReglement
        ? `${commande.ref} n’attend plus son règlement : elle revient dans « À faire ».`
        : `${commande.ref} attend son règlement : elle passe dans « En cours » jusqu’au paiement.`);
    } catch (e) {
      ajouter(e instanceof ErreurApi ? e.message : 'L’attente n’a pas pu être modifiée.', 'bad');
    }
  };

  /** Inverse la case ; le serveur efface la coche si elle rejoint les données. */
  const basculer = (cle: EtapeCochable) => async () => {
    try {
      await cocher.mutateAsync({ id: commande.id, etape: cle, coche: !cases[cle].fait });
    } catch (e) {
      ajouter(e instanceof ErreurApi ? e.message : 'La case n’a pas pu être modifiée.', 'bad');
    }
  };

  /** Ce qu'il faut ajouter au détail quand une main a tranché. */
  const main = (cle: EtapeCochable): Pick<ProprietesEtape, 'manuel'> => {
    const m = cases[cle].manuel;
    return m
      ? {
        manuel: `${m.valeur ? 'Cochée' : 'Décochée'} à la main${m.par ? ` par ${m.par}` : ''} `
          + `le ${dateHeure(m.le)}. Les données disent : ${cases[cle].automatique ? 'fait' : 'pas fait'}.`,
      }
      : {};
  };

  const etapes: ProprietesEtape[] = [
    {
      fait: cases.reglement.fait,
      libelle: 'Règlement',
      alerte: !cases.reglement.fait && paiement !== null && !paiement.regle,
      detail: (!paiement
        ? (sellsy ? 'Pas encore relevé chez Sellsy' : 'Payée à la commande sur Shopify')
        : paiement.regle ? 'Facture réglée'
        : `Facture non réglée${paiement.resteDu !== null ? ` — reste dû ${paiement.resteDu} €` : ''}`)
        // La relance se lit à côté du statut : c'est la première question
        // devant un impayé — « a-t-on déjà écrit au client ? ».
        + (relanceReglement.nombre && relanceReglement.derniereLe
          ? ` · relancé le ${formatCourt(relanceReglement.derniereLe.slice(0, 10))}`
            + (relanceReglement.nombre > 1 ? ` (${relanceReglement.nombre} fois)` : '')
          : '')
        + (enAttenteReglement
          ? ` · en attente depuis le ${formatCourt(commande.attenteReglement!.depuis.slice(0, 10))}, `
            + 'statut relevé deux fois par jour'
          : ''),
      action: (attenteProposable || commande.attenteReglement) ? (
        <button
          type="button" className="btn sm"
          disabled={attente.isPending}
          title={enAttenteReglement
            ? 'La commande revient dans « À faire », à passer après confirmation.'
            : 'La commande passe dans « En cours » et revient dans « À faire » dès que '
              + 'Sellsy la dit réglée.'}
          onClick={() => void basculerAttente()}
        >
          {enAttenteReglement ? 'Ne plus attendre' : 'Attendre le règlement'}
        </button>
      ) : undefined,
      badge: relanceReglement.nombre > 0 && !cases.reglement.fait ? 'relancé' : undefined,
      onClick: gestes.onReglement,
      onBasculer: basculer('reglement'),
      ...main('reglement'),
    },
    {
      fait: cases.fournisseur.fait,
      libelle: 'Commande fournisseur',
      detail: !cases.fournisseur.automatique
        ? (commande.fournisseurs.length ? `À passer à ${commande.fournisseurs.join(', ')}` : 'À passer')
        : `Transmise${commande.dateEnvoiFournisseur ? ` le ${formatCourt(commande.dateEnvoiFournisseur)}` : ''}`
          + etatMail(envois.fournisseur, false),
      onClick: gestes.onFournisseur,
      onBasculer: basculer('fournisseur'),
      ...main('fournisseur'),
    },
    /*
     * Chez HIOLLE seulement : la marchandise y attend qu'on fasse venir DHL.
     * Le libellé ouvre la demande — composée, relue, puis réservée —, qui
     * vivait dans un encart à part en bas de fiche.
     */
    ...(chezHiolle(commande.fournisseurs) ? [{
      fait: cases.enlevement.fait,
      libelle: 'Demande d’enlèvement',
      detail: commande.enlevement?.incertain
        ? `Demandé chez ${commande.enlevement.transport === 'dpd' ? 'DPD' : 'DHL'}, issue inconnue — à vérifier sur le portail`
        : commande.enlevement
        ? `Réservé chez ${commande.enlevement.transport === 'dpd' ? 'DPD' : 'DHL'} — envoi ${commande.enlevement.envoiId}`
          + (commande.enlevement.jourEnlevement
            ? `, enlèvement le ${formatCourt(commande.enlevement.jourEnlevement.slice(0, 10))}`
            : '')
        : cases.enlevement.fait ? 'Demandé hors de l’outil'
        : cases.fournisseur.fait ? 'À demander'
        : 'Après la commande fournisseur',
      onClick: commande.livre ? undefined : gestes.onEnlevement,
      onBasculer: basculer('enlevement'),
      ...main('enlevement'),
    }] : []),
    {
      fait: cases.expedition.fait,
      libelle: 'Date d’expédition',
      detail: commande.dateExpedition
        ? `${formatCourt(commande.dateExpedition)}${etatMail(envois.expedition, true)}`
        : 'Pas encore connue',
      onClick: gestes.onExpedition,
      onBasculer: basculer('expedition'),
      ...main('expedition'),
    },
    {
      fait: cases.tracking.fait,
      libelle: 'Tracking',
      detail: commande.tracking
        ? `${commande.transporteur ? `${commande.transporteur} · ` : ''}${commande.tracking}`
          + etatMail(envois.tracking, true)
        : 'Pas encore de numéro',
      onClick: gestes.onTracking,
      onBasculer: basculer('tracking'),
      ...main('tracking'),
    },
    {
      fait: commande.livre,
      libelle: 'Livré',
      detail: commande.livre
        ? `Livrée${commande.dateStatutTransporteur ? ` le ${formatCourt(commande.dateStatutTransporteur)}` : ''}`
        : (commande.statutTransporteur ?? 'Pas encore livrée'),
      onClick: gestes.onLivre,
      onBasculer: gestes.onBasculerLivre,
    },
    {
      fait: cases.avis.fait,
      libelle: 'Avis Google',
      detail: avis.envoye ? `Demandé${avis.envoyeLe ? ` le ${formatCourt(avis.envoyeLe)}` : ''}`
        // Échue sans être partie : le passage de 10 h ne l'a pas envoyée. On le
        // dit en rouge plutôt que de la montrer faite.
        : avisEchu(commande) ? `Prévu le ${formatCourt(avis.programmePour)}, pas encore parti`
        : avis.demande ? `Programmé pour le ${formatCourt(avis.programmePour)}`
        : 'Non demandé',
      alerte: !avis.envoye && avisEchu(commande),
      onClick: gestes.onAvis,
      onBasculer: basculer('avis'),
      ...main('avis'),
      verrou: avis.envoye || avisEchu(commande)
        ? 'La demande d’avis est partie : l’étape ne se décoche plus.'
        : undefined,
    },
  ];

  /*
   * La cassure : une commande en attente s'arrête AVANT l'étape qui suit.
   *
   * Placée devant la première case non cochée, elle dit à la fois où le
   * processus s'est interrompu et quand il doit reprendre. Échue, elle reste
   * affichée — l'attente n'a pas été levée — mais le dit.
   */
  const pause = commande.attenteJusquAu && !commande.livre
    ? etapes.findIndex((e) => !e.fait)
    : -1;

  return (
    <>
      <ul className="etapes" aria-label="Étapes de la commande">
        {etapes.map((e, i) => (
          <Fragment key={e.libelle}>
            {i === pause && (
              <li className="cassure">
                <span className="coche" aria-hidden="true">⏸</span>
                <div>
                  <strong>
                    En attente jusqu’au {formatCourt(commande.attenteJusquAu)}
                    {commande.calcul.actions.some((a) => a.cle === 'attente') && ' — échue'}
                  </strong>
                  {/* Ce qu'on attend se lit à côté de la date : c'est ce qui
                      dira, le jour venu, quoi vérifier avant de reprendre. */}
                  {commande.attenteMotif && <> · {commande.attenteMotif}</>}
                  <div className="sub">Le processus reprend à l’étape suivante.</div>
                  {/* Reprendre se tranche ici, là où l'attente coupe les étapes —
                      une attente échue comprise. */}
                  <Repousser commande={commande} />
                </div>
              </li>
            )}
            <Etape {...e} occupe={cocher.isPending} />
          </Fragment>
        ))}
      </ul>
      {/* Sans cassure, « Repousser » se tient sous les cases : repoussée, la
          commande montre déjà « Reprendre » dans la cassure, pas deux fois. */}
      {pause === -1 && (
        <div className="etapes-pied">
          <Repousser commande={commande} />
        </div>
      )}
    </>
  );
}

/** L'état du mail lié, en complément d'une étape déjà franchie. */
function etatMail(envoi: EtatEnvoiCommande, client: boolean): string {
  switch (envoi.etat) {
    case 'envoye':
      return envoi.deposeLe ? ` · mail envoyé le ${formatCourt(envoi.deposeLe.slice(0, 10))}` : ' · mail envoyé';
    case 'a_envoyer': return client ? ' · client pas encore prévenu' : '';
    case 'echec': return ' · mail en échec';
    case 'ignore': return ' · mail écarté';
    default: return '';
  }
}

interface ProprietesEtape {
  fait: boolean;
  libelle: string;
  detail: ReactNode;
  onClick?: () => void;
  onBasculer: () => void;
  alerte?: boolean;
  /** Étiquette à côté du libellé — « relancé ». */
  badge?: string;
  /** Qui a coché à la main, et quand. Absent : la case suit les données. */
  manuel?: string;
  /** Un geste propre à l'étape, à droite du libellé. */
  action?: ReactNode;
  occupe?: boolean;
  /** Cochée et figée : la raison, au survol. */
  verrou?: string;
}

function Etape({
  fait, libelle, detail, onClick, onBasculer, alerte = false, badge, manuel, action, occupe = false,
  verrou,
}: ProprietesEtape) {
  const fige = Boolean(verrou) && fait;
  return (
    <li className={fait ? 'fait' : ''}>
      <button
        type="button" className={`coche${manuel ? ' manuelle' : ''}`}
        role="checkbox" aria-checked={fait}
        aria-label={`${libelle} : ${fait ? 'fait' : 'pas encore'}${manuel ? ', coché à la main' : ''}`}
        title={fige ? verrou : (manuel ?? (fait ? 'Décocher' : 'Cocher'))}
        disabled={occupe || fige}
        onClick={onBasculer}
      >
        {fait ? '✓' : ''}
      </button>
      <div>
        {onClick ? (
          <button type="button" className="lien-etape" onClick={onClick}>{libelle}</button>
        ) : (
          <span className="lien-etape inerte">{libelle}</span>
        )}
        {badge && <span className="tag" style={{ marginLeft: 8 }}>{badge}</span>}
        {action && <span style={{ marginLeft: 8 }}>{action}</span>}
        <div className="sub" style={alerte ? { color: 'var(--brick)' } : undefined}>{detail}</div>
      </div>
    </li>
  );
}

/** Fenêtre commune aux deux étapes sans mail : règlement et avis. */
function Fenetre({
  titre, onFermer, children, pied,
}: {
  titre: string;
  onFermer: () => void;
  children: ReactNode;
  /** Calé à gauche du pied, avant « Fermer ». */
  pied?: ReactNode;
}) {
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={titre} style={{ width: 520 }}>
        <div className="mhead">
          <h2>{titre}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>
        <div className="mbody">{children}</div>
        <div className="mfoot">
          {pied}
          <button type="button" className="btn pri" onClick={onFermer}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Le règlement : il n'y a pas de mail à envoyer, mais un statut à lire et à
 * actualiser. C'est ce que faisait l'encadré de la fiche, rangé derrière sa case.
 */
export function FenetreReglement({
  commande, onFermer,
}: {
  commande: CommandeDetaillee;
  onFermer: () => void;
}) {
  const { paiement, relanceReglement } = commande;
  const sellsy = estFactureSellsy(commande.ref);
  /*
   * Une fenêtre à la fois : relire ou composer une relance REMPLACE le statut,
   * puis y revient. Deux fenêtres empilées se fermeraient ensemble à Échap.
   */
  const [vue, setVue] = useState<'statut' | 'relancer' | 'relances'>('statut');
  if (vue === 'relancer') return <RelanceReglement commande={commande} onFermer={() => setVue('statut')} />;
  if (vue === 'relances') {
    return <RelancesReglementDeposees commande={commande} onFermer={() => setVue('statut')} />;
  }

  return (
    <Fenetre titre={`Règlement — ${commande.ref}`} onFermer={onFermer} pied={<Repousser commande={commande} />}>
      {paiement ? (
        <>
          <dl className="kv" style={{ marginBottom: 10 }}>
            <dt>Statut Sellsy</dt><dd>{paiement.statut || '—'}</dd>
            {paiement.resteDu !== null && <><dt>Reste dû</dt><dd>{paiement.resteDu} €</dd></>}
            <dt>Relevé le</dt><dd>{dateHeure(paiement.releveLe)}</dd>
          </dl>
          {!paiement.regle && (
            <p className="err" style={{ margin: 0 }}>
              Facture non réglée : la commande reste dans « À faire », et passer la commande au
              fournisseur demandera une confirmation.
            </p>
          )}
        </>
      ) : sellsy ? (
        <p className="hint" style={{ margin: 0 }}>
          Le règlement de cette commande n’a jamais été relevé chez Sellsy. Elle n’est donc pas
          signalée — tant qu’on ne sait pas, on ne suppose pas un impayé. Le relevé automatique
          repasse dans l’heure.
        </p>
      ) : (
        /* Shopify encaisse à la commande : la commande est réglée par défaut,
           sans facture Sellsy à relever. Si le paiement pose question, la case
           se décoche à la main. */
        <p className="hint" style={{ margin: 0 }}>
          Commande de la boutique Shopify, payée à la commande : elle est réglée par défaut.
          Décochez la case si le paiement pose problème.
        </p>
      )}
      {sellsy && <ReleverPaiement commandeId={commande.id} />}

      {/* Relancer le client : seulement tant que la facture n'est pas réglée.
          L'inconnu compte comme non réglé ici — c'est un choix humain, pas une
          règle du moteur, et le texte se relit avant de partir. */}
      {!commande.etapes.reglement.fait && (
        <div className="fieldset" style={{ margin: '14px 0 0' }}>
          <h3 style={{ marginTop: 0 }}>Relance du client</h3>
          <p className="hint" style={{ margin: '0 0 10px' }}>
            {relanceReglement.nombre === 0
              ? 'Le client n’a pas encore été relancé pour ce règlement.'
              : `Relancé ${relanceReglement.nombre} fois, la dernière le `
                + `${dateHeure(relanceReglement.derniereLe!)}.`}
          </p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button type="button" className="btn pri sm" onClick={() => setVue('relancer')}>
              {relanceReglement.nombre ? 'Relancer à nouveau' : 'Relancer le client'}
            </button>
            {relanceReglement.nombre > 0 && (
              <button type="button" className="btn sm" onClick={() => setVue('relances')}>
                Voir {relanceReglement.nombre > 1 ? 'les relances envoyées' : 'la relance envoyée'}
              </button>
            )}
          </div>
        </div>
      )}
    </Fenetre>
  );
}

/** L'avis Google : la même case que la liste, qui programme ou décoche. */
export function FenetreAvis({
  commande, onFermer,
}: {
  commande: CommandeDetaillee;
  onFermer: () => void;
}) {
  return (
    <Fenetre titre={`Avis Google — ${commande.ref}`} onFermer={onFermer}>
      <CaseAvis commande={commande} />
      {commande.avis.erreur && <p className="err" style={{ margin: '10px 0 0' }}>{commande.avis.erreur}</p>}
    </Fenetre>
  );
}

function ReleverPaiement({ commandeId }: { commandeId: string }) {
  const { ajouter } = useToasts();
  const relever = useReleverPaiement();

  return (
    <button
      type="button" className="btn sm" style={{ marginTop: 10 }}
      disabled={relever.isPending}
      onClick={async () => {
        try {
          const r = await relever.mutateAsync(commandeId);
          // Le message dit ce qui a changé, ou que rien n'a changé : un bouton
          // qui ne répond rien laisse croire qu'il n'a pas marché.
          ajouter(r.indisponible ?? r.message, r.paiement?.regle ? 'ok' : 'bad');
        } catch (e) {
          ajouter(e instanceof ErreurApi ? e.message : 'Le relevé a échoué.', 'bad');
        }
      }}
    >
      {relever.isPending
        ? <><span className="spin" /> Relevé…</>
        : 'Actualiser le statut de la facture'}
    </button>
  );
}
